/**
 * Lightweight client-side geocoding for the create-project search box.
 * - Postal codes: geoapi.pt (official PT postal DB, centroid) with Nominatim fallback.
 * - Free text (city / freguesia / concelho / address): Nominatim (PT-scoped).
 *
 * Phase A (search + navigate). Cadastre search (NIC / artigo / our freguesia
 * data) is a separate backend endpoint, see docs/requirements/finder_search_plan.md.
 */

export interface GeoResult {
  label: string;
  lat: number;
  lon: number;
  /** [west, south, east, north] in lng/lat, when available. */
  bbox?: [number, number, number, number];
  kind: 'postal' | 'place';
}

/** XXXX or XXXX-XXX */
export function isPostalCode(s: string): boolean {
  return /^\d{4}(-\d{3})?$/.test(s.trim());
}

async function geocodePostal(code: string): Promise<GeoResult | null> {
  // geoapi.pt — every PT postal code with a centroid
  try {
    const r = await fetch(`https://json.geoapi.pt/cp/${encodeURIComponent(code)}`);
    if (r.ok) {
      const d = await r.json();
      if (d.centro) {
        const label = [d['Designação Postal'], d.Concelho, d.Distrito].filter(Boolean).join(', ');
        return { lat: d.centro[0], lon: d.centro[1], label: `${code} - ${label}`, kind: 'postal' };
      }
    }
  } catch { /* fall through */ }
  // Nominatim fallback
  try {
    const r = await fetch(
      `https://nominatim.openstreetmap.org/search?postalcode=${encodeURIComponent(code)}&countrycodes=pt&format=jsonv2&limit=1`,
      { headers: { Accept: 'application/json' } },
    );
    if (r.ok) {
      const d = await r.json();
      if (d.length) return { lat: +d[0].lat, lon: +d[0].lon, label: d[0].display_name, kind: 'postal' };
    }
  } catch { /* give up */ }
  return null;
}

/** Unified search: postal code -> geoapi.pt; otherwise PT-scoped Nominatim. */
export async function searchPlaces(q: string): Promise<GeoResult[]> {
  const query = q.trim();
  if (!query) return [];
  if (isPostalCode(query)) {
    const r = await geocodePostal(query);
    return r ? [r] : [];
  }
  try {
    const r = await fetch(
      `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}&countrycodes=pt&format=jsonv2&limit=6&addressdetails=0`,
      { headers: { Accept: 'application/json' } },
    );
    if (!r.ok) return [];
    const d = await r.json();
    return d.map((x: any): GeoResult => ({
      label: x.display_name,
      lat: +x.lat,
      lon: +x.lon,
      // Nominatim boundingbox = [south, north, west, east]
      bbox: x.boundingbox
        ? [+x.boundingbox[2], +x.boundingbox[0], +x.boundingbox[3], +x.boundingbox[1]]
        : undefined,
      kind: 'place',
    }));
  } catch {
    return [];
  }
}
